import type { TransportCharge } from "../../services/transportService";
import type { HostelPayment } from "../../services/hostelService";
import type { MealOrder, StudentMealPlan } from "../../services/cafeteriaService";
import type { Order as UniformOrder } from "../../services/uniformService";
import { StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { Field } from "../FormLayout";
import { byNumber, byText, humanise, reversed, statusDot, type RegisterConfig } from "../register/registerLogic";

/**
 * Views, sorts and columns for the service-charge registers (transport,
 * boarding, cafeteria, uniforms). What can be done with a row stays on the
 * Financial page, next to the handlers that do it.
 */

const money = (value: string | number | null | undefined) => Number(value || 0).toLocaleString();
const today = () => new Date().toISOString().slice(0, 10);

/** Unpaid and past its due date, whether or not the server has flagged it yet. */
export const chargeIsOverdue = (charge: { status: string; due_date: string }) =>
  charge.status === "overdue" || (charge.status === "pending" && charge.due_date < today());

export const chargeStatus = (charge: { status: string; due_date: string }) =>
  chargeIsOverdue(charge) ? { label: "Overdue", dot: statusDot("overdue") } : { label: humanise(charge.status), dot: statusDot(charge.status) };

export const chargeIsOpen = (charge: { status: string }) => ["pending", "overdue"].includes(charge.status);

type Charge = { status: string; due_date: string; amount: string; student_name: string };

const chargeViews = <T extends Charge>() => [
  { key: "all", label: "All" },
  { key: "open", label: "Awaiting payment", match: (row: T) => chargeIsOpen(row) },
  { key: "overdue", label: "Overdue", match: (row: T) => chargeIsOverdue(row) },
  { key: "paid", label: "Paid", match: (row: T) => row.status === "paid" },
  { key: "cancelled", label: "Cancelled", match: (row: T) => ["cancelled", "refunded"].includes(row.status) },
];

const chargeSorts = <T extends Charge>() => [
  { key: "due", label: "Due soonest", compare: byText<T>((row) => row.due_date) },
  { key: "-due", label: "Due latest", compare: reversed(byText<T>((row) => row.due_date)) },
  { key: "-amount", label: "Amount (highest first)", compare: reversed(byNumber<T>((row) => row.amount)) },
  { key: "student", label: "Student (A–Z)", compare: byText<T>((row) => row.student_name) },
];

// Transport ------------------------------------------------------------------

export const transportConfig: RegisterConfig<TransportCharge> = {
  search: (row) => [row.student_name, row.student_id, row.route_name, row.invoice_number, row.reference_number, row.notes].join(" "),
  views: chargeViews<TransportCharge>(),
  date: { label: "Due", value: (row) => row.due_date },
  sorts: chargeSorts<TransportCharge>(),
};

export const transportColumns: RegisterColumn<TransportCharge>[] = [
  { header: "Student", cell: (row) => <span className="font-medium text-gray-900">{row.student_name}</span>, exportValue: (row) => row.student_name, className: "max-w-[14rem] truncate" },
  { header: "Route", cell: (row) => row.route_name, exportValue: (row) => row.route_name, className: "text-gray-700" },
  { header: "Invoice", cell: (row) => <span className="font-mono text-xs">{row.invoice_number || "—"}</span>, exportValue: (row) => row.invoice_number || "" },
  { header: "Due", cell: (row) => row.due_date, exportValue: (row) => row.due_date, className: "tabular-nums text-gray-700" },
  { header: "Amount (UGX)", cell: (row) => <span className="font-semibold text-gray-900">{money(row.amount)}</span>, exportValue: (row) => Number(row.amount), align: "right" },
  { header: "Status", cell: (row) => <StatusDot {...chargeStatus(row)} />, exportValue: (row) => chargeStatus(row).label },
  { header: "Paid on", cell: (row) => row.paid_date?.slice(0, 10) || "—", exportValue: (row) => row.paid_date?.slice(0, 10) ?? "", className: "tabular-nums text-gray-600" },
];

// Boarding -------------------------------------------------------------------

export const boardingConfig: RegisterConfig<HostelPayment> = {
  search: (row) => [row.student_name, row.student_id, row.hostel_name, row.fee_name, row.invoice_number, row.reference_number, row.notes].join(" "),
  views: chargeViews<HostelPayment>(),
  date: { label: "Due", value: (row) => row.due_date },
  sorts: chargeSorts<HostelPayment>(),
};

export const boardingColumns: RegisterColumn<HostelPayment>[] = [
  { header: "Student", cell: (row) => <span className="font-medium text-gray-900">{row.student_name}</span>, exportValue: (row) => row.student_name, className: "max-w-[14rem] truncate" },
  { header: "Hostel", cell: (row) => row.hostel_name, exportValue: (row) => row.hostel_name, className: "text-gray-700" },
  { header: "Fee", cell: (row) => row.fee_name, exportValue: (row) => row.fee_name, className: "max-w-[12rem] truncate text-gray-700" },
  { header: "Invoice", cell: (row) => <span className="font-mono text-xs">{row.invoice_number || "—"}</span>, exportValue: (row) => row.invoice_number || "" },
  { header: "Due", cell: (row) => row.due_date, exportValue: (row) => row.due_date, className: "tabular-nums text-gray-700" },
  { header: "Amount (UGX)", cell: (row) => <span className="font-semibold text-gray-900">{money(row.amount)}</span>, exportValue: (row) => Number(row.amount), align: "right" },
  { header: "Status", cell: (row) => <StatusDot {...chargeStatus(row)} />, exportValue: (row) => chargeStatus(row).label },
];

// Cafeteria: meal orders -------------------------------------------------------

export const mealOrderCanTakePayment = (order: MealOrder) =>
  order.payment_status === "pending" && !["served", "cancelled"].includes(order.status) && Number(order.amount) > 0;
export const mealOrderCanRefund = (order: MealOrder) =>
  order.payment_status === "paid" && ["pending", "confirmed"].includes(order.status);

export const mealOrderConfig: RegisterConfig<MealOrder> = {
  search: (row) => [row.student_name, row.daily_menu_name].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "unpaid", label: "Unpaid", match: (row) => row.payment_status === "pending" && row.status !== "cancelled" },
    { key: "paid", label: "Paid", match: (row) => row.payment_status === "paid" },
    { key: "refunded", label: "Refunded", match: (row) => row.payment_status === "refunded" },
    { key: "cancelled", label: "Cancelled", match: (row) => row.status === "cancelled" },
  ],
  date: { label: "Ordered", value: (row) => row.order_date },
  sorts: [
    { key: "-date", label: "Newest first", compare: reversed(byText<MealOrder>((row) => row.order_date)) },
    { key: "date", label: "Oldest first", compare: byText<MealOrder>((row) => row.order_date) },
    { key: "-amount", label: "Amount (highest first)", compare: reversed(byNumber<MealOrder>((row) => row.amount)) },
    { key: "student", label: "Student (A–Z)", compare: byText<MealOrder>((row) => row.student_name) },
  ],
};

export const mealOrderColumns: RegisterColumn<MealOrder>[] = [
  { header: "Ordered", cell: (row) => row.order_date?.slice(0, 10), exportValue: (row) => row.order_date?.slice(0, 10) ?? "", className: "tabular-nums text-gray-600" },
  { header: "Student", cell: (row) => <span className="font-medium text-gray-900">{row.student_name}</span>, exportValue: (row) => row.student_name, className: "max-w-[14rem] truncate" },
  { header: "Menu", cell: (row) => row.daily_menu_name, exportValue: (row) => row.daily_menu_name, className: "max-w-[14rem] truncate text-gray-700" },
  { header: "Amount (UGX)", cell: (row) => <span className="font-semibold text-gray-900">{money(row.amount)}</span>, exportValue: (row) => Number(row.amount), align: "right" },
  { header: "Order", cell: (row) => <StatusDot label={humanise(row.status)} dot={statusDot(row.status)} />, exportValue: (row) => humanise(row.status) },
  { header: "Payment", cell: (row) => <StatusDot label={humanise(row.payment_status)} dot={statusDot(row.payment_status)} />, exportValue: (row) => humanise(row.payment_status) },
];

// Cafeteria: meal plans ---------------------------------------------------------

export const mealPlanCanTakePayment = (plan: StudentMealPlan) => plan.status === "pending";
export const mealPlanCanRefund = (plan: StudentMealPlan) =>
  plan.status === "active" && plan.payment_status === "completed" && plan.meals_used === 0;

export const mealPlanConfig: RegisterConfig<StudentMealPlan> = {
  search: (row) => [row.student_name, row.meal_plan_name].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "pending", label: "Awaiting payment", match: (row) => row.status === "pending" },
    { key: "active", label: "Active", match: (row) => row.status === "active" },
    { key: "ended", label: "Ended", match: (row) => ["expired", "completed"].includes(row.status) },
    { key: "cancelled", label: "Cancelled", match: (row) => row.status === "cancelled" },
  ],
  date: { label: "Starts", value: (row) => row.start_date },
  sorts: [
    { key: "-start", label: "Newest first", compare: reversed(byText<StudentMealPlan>((row) => row.start_date)) },
    { key: "start", label: "Oldest first", compare: byText<StudentMealPlan>((row) => row.start_date) },
    { key: "student", label: "Student (A–Z)", compare: byText<StudentMealPlan>((row) => row.student_name) },
  ],
};

export const mealPlanColumns: RegisterColumn<StudentMealPlan>[] = [
  { header: "Student", cell: (row) => <span className="font-medium text-gray-900">{row.student_name}</span>, exportValue: (row) => row.student_name, className: "max-w-[14rem] truncate" },
  { header: "Plan", cell: (row) => row.meal_plan_name, exportValue: (row) => row.meal_plan_name, className: "max-w-[14rem] truncate text-gray-700" },
  { header: "Period", cell: (row) => `${row.start_date} → ${row.end_date}`, exportValue: (row) => `${row.start_date} to ${row.end_date}`, className: "tabular-nums text-gray-600" },
  { header: "Paid (UGX)", cell: (row) => money(row.amount_paid), exportValue: (row) => Number(row.amount_paid), align: "right" },
  { header: "Meals used", cell: (row) => row.meals_used, exportValue: (row) => row.meals_used, align: "right" },
  { header: "Status", cell: (row) => <StatusDot label={humanise(row.status)} dot={statusDot(row.status)} />, exportValue: (row) => humanise(row.status) },
  { header: "Payment", cell: (row) => <StatusDot label={humanise(row.payment_status)} dot={statusDot(row.payment_status)} />, exportValue: (row) => humanise(row.payment_status) },
];

// Uniforms ----------------------------------------------------------------------

export const uniformBalance = (order: UniformOrder) => Number(order.total_amount) - Number(order.paid_amount);
export const uniformCanTakePayment = (order: UniformOrder) =>
  uniformBalance(order) > 0 && !["cancelled", "delivered"].includes(order.status);
export const uniformItemsLabel = (order: UniformOrder) =>
  order.items.map((item) => `${item.uniform_item_name} ${item.uniform_item_size} ×${item.quantity}`).join(", ");

export const uniformConfig: RegisterConfig<UniformOrder> = {
  search: (row) => [row.order_number, row.student_name, row.student_class, uniformItemsLabel(row), row.notes].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "balance", label: "Balance due", match: (row) => uniformCanTakePayment(row) },
    { key: "paid", label: "Fully paid", match: (row) => uniformBalance(row) <= 0 && row.status !== "cancelled" },
    { key: "delivered", label: "Delivered", match: (row) => row.status === "delivered" },
    { key: "cancelled", label: "Cancelled", match: (row) => row.status === "cancelled" },
  ],
  date: { label: "Ordered", value: (row) => row.order_date },
  sorts: [
    { key: "-date", label: "Newest first", compare: reversed(byText<UniformOrder>((row) => row.order_date)) },
    { key: "date", label: "Oldest first", compare: byText<UniformOrder>((row) => row.order_date) },
    { key: "-balance", label: "Balance (highest first)", compare: reversed(byNumber<UniformOrder>(uniformBalance)) },
    { key: "student", label: "Student (A–Z)", compare: byText<UniformOrder>((row) => row.student_name) },
  ],
};

export const uniformColumns: RegisterColumn<UniformOrder>[] = [
  { header: "Order no.", cell: (row) => <span className="font-mono text-xs">{row.order_number}</span>, exportValue: (row) => row.order_number },
  { header: "Ordered", cell: (row) => row.order_date?.slice(0, 10), exportValue: (row) => row.order_date?.slice(0, 10) ?? "", className: "tabular-nums text-gray-600" },
  { header: "Student", cell: (row) => <span className="font-medium text-gray-900">{row.student_name}</span>, exportValue: (row) => row.student_name, className: "max-w-[12rem] truncate" },
  { header: "Items", cell: (row) => uniformItemsLabel(row), exportValue: uniformItemsLabel, className: "max-w-[16rem] truncate text-gray-600" },
  { header: "Total (UGX)", cell: (row) => money(row.total_amount), exportValue: (row) => Number(row.total_amount), align: "right" },
  {
    header: "Balance (UGX)",
    cell: (row) => (uniformBalance(row) > 0 ? <span className="font-semibold text-gray-900">{money(uniformBalance(row))}</span> : <span className="text-gray-300">—</span>),
    exportValue: uniformBalance,
    align: "right",
  },
  { header: "Status", cell: (row) => <StatusDot label={humanise(row.status)} dot={statusDot(row.status)} />, exportValue: (row) => humanise(row.status) },
];

/** The payment method choice in the service-charge payment forms. */
export function PaymentMethodField({ withCard = false }: { withCard?: boolean }) {
  return (
    <Field label="Method">
      <select name="method" className="input mt-1">
        <option value="cash">Cash</option>
        <option value="bank_transfer">Bank transfer</option>
        <option value="mobile_money">Mobile money</option>
        {withCard ? <option value="card">Card</option> : <option value="cheque">Cheque</option>}
      </select>
    </Field>
  );
}
